<template>
  <div class="flex flex-col items-center gap-4 lg:flex-row">
    <RfBaseCard
      v-for="chart in charts"
      :key="chart.title"
      class="flex w-full max-w-full min-w-px flex-1 flex-col gap-3"
    >
      <Transition mode="out-in">
        <RfBaseSkeleton v-if="skeleton" key="skeleton" height="29" width="189" />
        <h2 v-else key="title" class="text-heading-1 text-beige-90">{{ chart.title }}</h2>
      </Transition>
      <RfChartAdditional :data="chart.data" :skeleton="skeleton" alterNoData />
    </RfBaseCard>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { chartColors } from "@/utils/constants/ColorsConstants";
import { DISPLAYED_METRICS, getDisplayMetrics, getSurveySeriesData } from "@/utils/metricsHelpers";
import RfBaseSkeleton from "@/components/skeletons/RfBaseSkeleton.vue";
import RfChartAdditional from "../RfCharts/RfChartAdditional.vue";
import RfBaseCard from "@/components/cards/RfBaseCard.vue";

const props = defineProps({
  metrics: { type: Object, default: () => ({}) },
  metricsKey: { type: String, default: "since_start_date" },
  path: { type: Object, default: () => ({}) },
  selectedVariantId: { type: String, default: null },
  variants: { type: Array, default: () => [] },
  skeleton: { type: Boolean, default: false },
  isSurvey: { type: Boolean, default: false },
  surveyMetrics: { type: Object, default: () => ({}) },
  surveyInputs: { type: Object, default: () => ({ labels: [], values: [] }) },
});

const currentMetric = computed(() => props.metrics[props.metricsKey]);

// — Interactions —

const resolvedDisplayMetrics = computed(() => {
  const raw = getDisplayMetrics(props.path);
  return Object.fromEntries(
    Object.entries(raw).map(([key, label]) => [key, label || DISPLAYED_METRICS[key]]),
  );
});

const barColorMap = ref({});

watch(
  () => props.selectedVariantId,
  id => {
    const stateKey = id ?? "total";
    if (barColorMap.value[stateKey]) return;
    const keys = Object.keys(resolvedDisplayMetrics.value).filter(
      key => resolvedDisplayMetrics.value[key],
    );
    const colors = [...chartColors];
    barColorMap.value[stateKey] = Object.fromEntries(
      keys.map(key => [key, colors.splice(Math.floor(Math.random() * colors.length), 1)[0]]),
    );
  },
  { immediate: true },
);

const interactionDatasets = computed(() => {
  if (!currentMetric.value?.data?.length) return [];

  const displayMetrics = resolvedDisplayMetrics.value;
  const filtered = props.selectedVariantId
    ? currentMetric.value.data.filter(v => v.id === props.selectedVariantId)
    : props.variants;

  const interactionKeys = Object.keys(displayMetrics).filter(key => displayMetrics[key]);
  const counts = {};
  interactionKeys.forEach(key => {
    counts[key] = filtered.reduce((sum, v) => sum + (v[key]?.uniques ?? 0), 0);
  });

  const activeKeys = interactionKeys.filter(key => counts[key] > 0);
  const total = activeKeys.reduce((s, key) => s + counts[key], 0);
  if (!total) return [];

  const palette = barColorMap.value[props.selectedVariantId ?? "total"] ?? {};
  return activeKeys.map(key => ({
    label: displayMetrics[key],
    data: [{ key: 1, label: counts[key], raw: counts[key] / total }],
    backgroundColor: palette[key],
    order: 1,
    parsing: { xAxisKey: "raw", yAxisKey: "key" },
    props: { type: "horizontal" },
  }));
});

// — Survey —

const surveyFormatted = computed(() => {
  if (!props.isSurvey) return [];
  return getSurveySeriesData(props.surveyInputs, props.surveyMetrics);
});

const surveyColorMap = ref({});

watch(
  [() => props.selectedVariantId, surveyFormatted],
  ([id, formatted]) => {
    if (!formatted.length) return;
    const stateKey = id ?? "total";
    const state = surveyColorMap.value[stateKey] ?? {};
    const missing = formatted.filter(({ key }) => !state[key]);
    if (!missing.length) return;
    const free = [...chartColors].filter(color => !Object.values(state).includes(color));
    const freeCount = free.length;
    surveyColorMap.value[stateKey] = {
      ...state,
      ...Object.fromEntries(
        missing.map(({ key }, i) => [
          key,
          i < freeCount
            ? free.splice(Math.floor(Math.random() * free.length), 1)[0]
            : chartColors[i % chartColors.length],
        ]),
      ),
    };
  },
  { immediate: true },
);

const surveyDatasets = computed(() => {
  const total = surveyFormatted.value.reduce((s, d) => s + d.value, 0);
  if (!total) return [];
  const palette = surveyColorMap.value[props.selectedVariantId ?? "total"] ?? {};
  return surveyFormatted.value.map(({ key, value }) => ({
    label: key,
    data: [{ key: 1, label: value, raw: value / total }],
    backgroundColor: palette[key],
    order: 1,
    parsing: { xAxisKey: "raw", yAxisKey: "key" },
    props: { type: "horizontal" },
  }));
});

// — Visible charts —

const charts = computed(() => {
  const result = [];
  if (props.skeleton || interactionDatasets.value.length)
    result.push({ title: "Interactions", data: interactionDatasets.value });
  if (props.isSurvey && (props.skeleton || surveyDatasets.value.length))
    result.push({ title: "Survey Interactions", data: surveyDatasets.value });
  return result;
});
</script>
